import { httpClient } from '@/shared/api';
import type {
  CalcSchoolBandTuitionInputItem,
  NestedTuitionBandDto,
  SchoolBandWithTuitionBandsDto,
  TuitionBandEmbeddedSchoolDto,
} from './tuitionBand.dto';
import type { TuitionBandListItemDto, TuitionBandsPagedResult } from './tuitionBandList.dto';

const PREFIX = '/app/tuition-band';

function mapSchoolIdsRecord(raw: unknown): Record<string, number> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) out[k] = n;
  }
  return Object.keys(out).length ? out : undefined;
}

function mapTuitionBandSchool(raw: unknown): TuitionBandEmbeddedSchoolDto {
  if (!raw || typeof raw !== 'object') {
    return { id: '' };
  }
  const o = raw as Record<string, unknown>;
  const tuitionRaw = o.tuitionUSD ?? o.TuitionUSD;
  const tuitionUSD =
    typeof tuitionRaw === 'number' && Number.isFinite(tuitionRaw)
      ? tuitionRaw
      : Number(tuitionRaw) || 0;
  const sc = o.totalStudentCount ?? o.TotalStudentCount;
  return {
    id: String(o.id ?? o.Id ?? ''),
    schoolName: (o.schoolName ?? o.SchoolName) as string | null | undefined,
    educationalCenterNumber: (o.educationalCenterNumber ?? o.EducationalCenterNumber) as
      | string
      | null
      | undefined,
    code: (o.code ?? o.Code) as string | null | undefined,
    totalStudentCount:
      typeof sc === 'number' && Number.isFinite(sc) ? sc : Number(sc) || 0,
    tuitionUSD,
  };
}

function mapNestedSchoolIds(raw: unknown): string[] | undefined {
  if (Array.isArray(raw)) {
    const ids = raw.map((x) => String(x)).filter((id) => id.trim());
    return ids.length ? ids : undefined;
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const ids = Object.keys(raw as Record<string, unknown>).filter((k) => k.trim());
    return ids.length ? ids : undefined;
  }
  return undefined;
}

/**
 * Some APIs put the parent school-band GUID on nested tuition as `Id`; the real tuition row id is
 * often on `TuitionBandId` / `EntityId`. Prefer tuition-specific keys and drop values that echo the parent.
 */
function pickNestedTuitionBandId(
  o: Record<string, unknown>,
  parentSchoolBandId?: string
): string | undefined {
  const p = parentSchoolBandId?.trim().toLowerCase();
  const echoesParent = (s: string) => Boolean(p && s.trim().toLowerCase() === p);
  const candidates: unknown[] = [
    o.tuitionBandId ?? o.TuitionBandId,
    o.bandId ?? o.BandId,
    o.entityId ?? o.EntityId,
    o.id ?? o.Id,
  ];
  for (const c of candidates) {
    if (c == null || String(c) === '') continue;
    const s = String(c).trim();
    if (!s || echoesParent(s)) continue;
    return s;
  }
  return undefined;
}

function mapNestedTuitionBand(raw: unknown, parentSchoolBandId?: string): NestedTuitionBandDto {
  if (!raw || typeof raw !== 'object') {
    return { minValue: 0, maxValue: 0 };
  }
  const o = raw as Record<string, unknown>;
  const schoolIds = mapNestedSchoolIds(o.schoolIds ?? o.SchoolIds);
  const rawSchools = o.schools ?? o.Schools;
  const schools = Array.isArray(rawSchools)
    ? rawSchools.map(mapTuitionBandSchool).filter((s) => Boolean(s.id?.trim()))
    : undefined;
  const idRaw = pickNestedTuitionBandId(o, parentSchoolBandId);
  return {
    id: idRaw,
    name: (o.name ?? o.Name) as string | null | undefined,
    minValue: Number(o.minValue ?? o.MinValue ?? 0),
    maxValue: Number(o.maxValue ?? o.MaxValue ?? 0),
    description: (o.description ?? o.Description) as string | null | undefined,
    minSchoolsPerBand: Number(o.minSchoolsPerBand ?? o.MinSchoolsPerBand ?? 0),
    year: Number(o.year ?? o.Year ?? 0),
    schoolBandId:
      o.schoolBandId != null || o.SchoolBandId != null
        ? String(o.schoolBandId ?? o.SchoolBandId ?? '')
        : undefined,
    numberOfSchools: Number(o.numberOfSchools ?? o.NumberOfSchools ?? 0),
    numberOfStudents: Number(o.numberOfStudents ?? o.NumberOfStudents ?? 0),
    schoolIds,
    schools: schools?.length ? schools : undefined,
  };
}

function mapSchoolBandWithTuition(raw: unknown): SchoolBandWithTuitionBandsDto {
  if (!raw || typeof raw !== 'object') {
    return {
      minValue: 0,
      maxValue: 0,
      minSchoolsPerBand: 0,
      year: 0,
    };
  }
  const o = raw as Record<string, unknown>;
  const idRaw = o.id ?? o.Id;
  const parentKey =
    idRaw != null && String(idRaw) !== '' ? String(idRaw).trim() : undefined;
  const rawTbs = o.tuitionBands ?? o.TuitionBands;
  const tuitionBands = Array.isArray(rawTbs)
    ? rawTbs.map((tb) => mapNestedTuitionBand(tb, parentKey))
    : undefined;
  const sid = o.schoolIds ?? o.SchoolIds;
  const schoolIds = mapSchoolIdsRecord(sid);
  const rawParentSchools = o.schools ?? o.Schools;
  const parentSchools = Array.isArray(rawParentSchools)
    ? rawParentSchools.map(mapTuitionBandSchool).filter((s) => Boolean(s.id?.trim()))
    : undefined;
  return {
    id: idRaw != null && String(idRaw) !== '' ? String(idRaw) : undefined,
    name: (o.name ?? o.Name) as string | null | undefined,
    minValue: Number(o.minValue ?? o.MinValue ?? 0),
    maxValue: Number(o.maxValue ?? o.MaxValue ?? 0),
    description: (o.description ?? o.Description) as string | null | undefined,
    minSchoolsPerBand: Number(o.minSchoolsPerBand ?? o.MinSchoolsPerBand ?? 0),
    year: Number(o.year ?? o.Year ?? 0),
    numberOfSchools: Number(o.numberOfSchools ?? o.NumberOfSchools ?? 0),
    schoolIds,
    schools: parentSchools?.length ? parentSchools : undefined,
    tuitionBands,
  };
}

function normalizeSchoolBandWithTuitionBandsList(
  raw: unknown
): SchoolBandWithTuitionBandsDto[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(mapSchoolBandWithTuition);
}

function mapTuitionBandListItem(raw: unknown): TuitionBandListItemDto {
  if (!raw || typeof raw !== 'object') {
    return {
      id: '',
      name: '',
      minValue: 0,
      maxValue: 0,
      minSchoolsPerBand: 0,
      year: 0,
      schoolBandId: '',
    };
  }
  const o = raw as Record<string, unknown>;
  return {
    id: String(o.id ?? o.Id ?? ''),
    name: String(o.name ?? o.Name ?? ''),
    fullName: (o.fullName ?? o.FullName) as string | null | undefined,
    minValue: Number(o.minValue ?? o.MinValue ?? 0),
    maxValue: Number(o.maxValue ?? o.MaxValue ?? 0),
    description: (o.description ?? o.Description) as string | null | undefined,
    minSchoolsPerBand: Number(o.minSchoolsPerBand ?? o.MinSchoolsPerBand ?? 0),
    year: Number(o.year ?? o.Year ?? 0),
    schoolBandId: String(o.schoolBandId ?? o.SchoolBandId ?? ''),
    numberOfSchools: Number(o.numberOfSchools ?? o.NumberOfSchools ?? 0) || undefined,
    numberOfStudents: Number(o.numberOfStudents ?? o.NumberOfStudents ?? 0) || undefined,
  };
}

function normalizeTuitionBandsPagedResult(raw: unknown): TuitionBandsPagedResult {
  if (!raw || typeof raw !== 'object') {
    return { items: [], totalCount: 0 };
  }
  const o = raw as Record<string, unknown>;
  const rawItems = (o.items ?? o.Items) as unknown[] | undefined;
  const items = Array.isArray(rawItems)
    ? rawItems.map(mapTuitionBandListItem).filter((b) => Boolean(b.id?.trim()))
    : [];
  const tc = o.totalCount ?? o.TotalCount;
  const totalCount = typeof tc === 'number' && Number.isFinite(tc) ? tc : items.length;
  return { items, totalCount };
}

function tuitionBandListQuery(params: {
  Year: number;
  SkipCount?: number;
  MaxResultCount?: number;
}): string {
  const search = new URLSearchParams();
  search.set('Year', String(params.Year));
  if (params.SkipCount != null) search.set('SkipCount', String(params.SkipCount));
  if (params.MaxResultCount != null) search.set('MaxResultCount', String(params.MaxResultCount));
  const q = search.toString();
  return q ? `?${q}` : '';
}

export const tuitionBandApi = {
  /**
   * GET /api/app/tuition-band?Year=… — flat tuition band list for filters.
   */
  getList: async (params: {
    year: number;
    skipCount?: number;
    maxResultCount?: number;
  }): Promise<TuitionBandsPagedResult> => {
    const raw = await httpClient.get<unknown>(
      `${PREFIX}${tuitionBandListQuery({
        Year: params.year,
        SkipCount: params.skipCount ?? 0,
        MaxResultCount: params.maxResultCount ?? 500,
      })}`
    );
    return normalizeTuitionBandsPagedResult(raw);
  },

  /**
   * POST /api/app/tuition-band/calc-school-band-tuition-bands
   * Derives nested tuition bands per school (size) band.
   */
  calcSchoolBandTuitionBands: async (items: CalcSchoolBandTuitionInputItem[]) => {
    const raw = await httpClient.post<unknown>(
      `${PREFIX}/calc-school-band-tuition-bands`,
      items
    );
    return normalizeSchoolBandWithTuitionBandsList(raw);
  },

  /**
   * POST /api/app/tuition-band/rebuild-schools-for-bands
   * Reassigns schools to tuition bands from the full school-band tree.
   */
  rebuildSchoolsForBands: async (items: SchoolBandWithTuitionBandsDto[]) => {
    const raw = await httpClient.post<unknown>(
      `${PREFIX}/rebuild-schools-for-bands`,
      items
    );
    return normalizeSchoolBandWithTuitionBandsList(raw);
  },
};
